// WishlistPage.js — Mobile: full-screen vertical swiper. Desktop: horizontal scrolling video cards
import React, { useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
  Chip,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, Keyboard } from "swiper/modules";
import "swiper/css";
import DeleteIcon from "@mui/icons-material/Delete";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ShareIcon from "@mui/icons-material/Share";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";

/* ───────── DESKTOP CARD (hover-to-play video) ───────── */
const DesktopCard = ({ item, onNavigate, onAddToCart, onToggle, onShare }) => {
  const itemId = item.id || item._id || item.productId;
  const imageUrl = item.imageUrl || item.images?.[0]?.thumbnailUrl || "";
  const videoUrl = item.videoUrl || "";
  const videoRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [mediaFailed, setMediaFailed] = useState(false);

  const handleMouseEnter = useCallback(() => {
    if (!videoUrl) return;
    setHovering(true);
    if (videoRef.current) { videoRef.current.currentTime = 0; videoRef.current.play().catch(() => {}); }
  }, [videoUrl]);

  const handleMouseLeave = useCallback(() => {
    setHovering(false);
    if (videoRef.current) { videoRef.current.pause(); }
  }, []);

  const showPoster = !hovering;
  const showVideo = hovering;

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ borderRadius: 3, overflow: "hidden", bgcolor: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", transition: "transform 0.2s, box-shadow 0.2s", "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 40px rgba(0,0,0,0.5)" } }}
    >
      <Box sx={{ position: "relative", width: "100%", paddingTop: "125%", bgcolor: "#111", cursor: "pointer", overflow: "hidden" }} onClick={() => onNavigate(`/product/${itemId}`)}>
        {!mediaFailed && videoUrl ? (
          <>
            {imageUrl && (
              <Box component="img" src={imageUrl} alt={item.title} onError={() => setMediaFailed(true)} sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", opacity: showPoster ? 1 : 0, transition: "opacity 0.3s" }} />
            )}
            <Box component="video" ref={videoRef} src={videoUrl} muted loop playsInline preload="auto" onError={() => setMediaFailed(true)} sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", opacity: showVideo ? 1 : (imageUrl ? 0 : 1), transition: "opacity 0.3s" }} />
          </>
        ) : !mediaFailed && imageUrl ? (
          <Box component="img" src={imageUrl} alt={item.title} onError={() => setMediaFailed(true)} sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#222", color: "rgba(255,255,255,0.4)" }}>
            <Box sx={{ fontSize: 48, mb: 1 }}>🎬</Box>
            <Typography sx={{ fontSize: 11, textAlign: "center", px: 1 }}>{item.title}</Typography>
          </Box>
        )}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "50%", background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)" }} />
        <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(item); }} sx={{ bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}><FavoriteIcon sx={{ fontSize: 18, color: "#ef4444" }} /></IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onShare(item); }} sx={{ bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}><ShareIcon sx={{ fontSize: 16, color: "#fff" }} /></IconButton>
        </Box>
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</Typography>
        </Box>
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography sx={{ color: "#FFD814", fontWeight: 700, fontSize: 16, mb: 1 }}>₹{Number(item.price || 0).toLocaleString("en-IN")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" size="small" onClick={() => onAddToCart(item)} sx={{ flex: 1, bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: "100px", minHeight: 32, "&:hover": { bgcolor: "#e6c512" } }}><ShoppingBagIcon sx={{ fontSize: 14, mr: 0.5 }} /> Cart</Button>
          <Button variant="outlined" size="small" onClick={() => onNavigate(`/delivery-location/${itemId}`)} sx={{ flex: 1, borderColor: "rgba(255,255,255,0.2)", color: "#fff", textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: "100px", minHeight: 32, "&:hover": { borderColor: "#FFD814", color: "#FFD814" } }}>Buy</Button>
        </Box>
      </Box>
    </Box>
  );
};

/* ───────── MOBILE REEL (full-screen, auto-play video when active) ───────── */
const MobileReelCard = ({ item, isActive, onNavigate, onAddToCart, onToggle, onShare }) => {
  const itemId = item.id || item._id || item.productId;
  const imageUrl = item.imageUrl || item.images?.[0]?.thumbnailUrl || "";
  const videoUrl = item.videoUrl || "";
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;
    if (isActive) { video.currentTime = 0; video.play().catch(() => {}); setVideoPlaying(true); }
    else { video.pause(); setVideoPlaying(false); }
  }, [isActive, videoUrl]);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", bgcolor: "#000", overflow: "hidden" }}>
      {videoUrl ? (
        <video ref={videoRef} src={videoUrl} muted loop playsInline preload="metadata" poster={imageUrl || undefined}
          onClick={() => { if (videoRef.current) { if (videoPlaying) { videoRef.current.pause(); } else { videoRef.current.play().catch(() => {}); } setVideoPlaying(!videoPlaying); } }}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : imageUrl ? (
        <Box component="img" src={imageUrl} alt={item.title} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: 40, opacity: 0.3 }}>📦</Box>
      )}

      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onShare(item); }}
        sx={{ position: "absolute", top: 100, right: 16, zIndex: 10, bgcolor: "rgba(0,0,0,0.6)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
        <ShareIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(item); }}
        sx={{ position: "absolute", top: 152, right: 16, zIndex: 10, bgcolor: "rgba(0,0,0,0.6)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
        <FavoriteIcon sx={{ fontSize: 20, color: "#ef4444" }} />
      </IconButton>

      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "35%", background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)" }} />
      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 1.5 }}>
        <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14, mb: 0.3 }}>{item.title}</Typography>
        <Typography sx={{ color: "#FFD814", fontWeight: 700, fontSize: 16, mb: 0.5 }}>₹{Number(item.price || 0).toLocaleString("en-IN")}</Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button variant="contained" size="small" onClick={() => onAddToCart(item)} sx={{ flex: 1, bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, fontSize: 11, borderRadius: "100px", py: 0.5, minHeight: 28, "&:hover": { bgcolor: "#e6c512" } }}><ShoppingBagIcon sx={{ fontSize: 13, mr: 0.3 }} /> Cart</Button>
          <Button variant="outlined" size="small" onClick={() => onNavigate(`/delivery-location/${itemId}`)} sx={{ flex: 1, borderColor: "rgba(255,255,255,0.3)", color: "#fff", textTransform: "none", fontWeight: 600, fontSize: 11, borderRadius: "100px", py: 0.5, minHeight: 28, "&:hover": { borderColor: "#FFD814", color: "#FFD814" } }}>Buy</Button>
        </Box>
      </Box>
    </Box>
  );
};

/* ───────── MAIN PAGE ───────── */
const WishlistPage = () => {
  const { wishlist, clearWishlist, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [activeMobileIndex, setActiveMobileIndex] = useState(0);

  const sanitizePrice = (p) => Number(String(p).replace(/,/g, "")) || 0;

  const handleShare = (item) => {
    const productId = item.id || item._id || item.productId;
    const productUrl = `${window.location.origin}/product/${productId}`;
    const shareText = `Check out "${item.title}" ₹${Number(item.price).toLocaleString("en-IN")}`;
    if (navigator.share) { navigator.share({ title: item.title, text: shareText, url: productUrl }).catch(() => {}); }
    else { navigator.clipboard.writeText(`${shareText}\n\n${productUrl}`).then(() => alert("Link copied to clipboard!")).catch(() => {}); }
  };

  const handleAddToCart = (item) => {
    addToCart({ id: item.id || item._id || item.productId, title: item.title, price: sanitizePrice(item.price), imageUrl: item.imageUrl || item.images?.[0]?.thumbnailUrl || "", qty: 1 });
  };

  return (
    <>
      <style>{`.swiper-slide { margin: 0 !important; height: 100% !important; } .swiper-wrapper { height: 100% !important; }`}</style>
      <Box sx={{ minHeight: "100vh", bgcolor: "#0f0f0f", pb: { xs: 0, sm: 4 } }}>
        {/* HEADER (hidden on mobile swiper) */}
        <Box sx={{ px: { xs: 2, sm: 4 }, pt: 3, pb: 2, display: { xs: "none", sm: "block" } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.3)" }} />} aria-label="breadcrumb">
            <Link underline="hover" sx={{ color: "rgba(255,255,255,0.6)", cursor: "pointer", display: "flex", alignItems: "center", fontSize: 14 }} onClick={(e) => { e.preventDefault(); navigate("/customer/dashboard"); }}>
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} /> Dashboard
            </Link>
            <Typography sx={{ color: "#fff", fontSize: 14, display: "flex", alignItems: "center" }}>❤️ Wishlist</Typography>
          </Breadcrumbs>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap", gap: 1 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: "#fff" }}>Saved Items<Chip label={`${wishlist.length} item${wishlist.length !== 1 ? "s" : ""}`} size="small" sx={{ ml: 1.5, bgcolor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)", fontWeight: 500, fontSize: 12 }} /></Typography>
            {wishlist.length > 0 && (<Button size="small" onClick={clearWishlist} sx={{ color: "rgba(255,255,255,0.5)", textTransform: "none", fontSize: 13, "&:hover": { color: "#ef4444" } }}><DeleteIcon sx={{ fontSize: 16, mr: 0.5 }} /> Clear all</Button>)}
          </Box>
        </Box>

        {/* EMPTY STATE */}
        {wishlist.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", mt: 8, px: 2 }}>
            <Box sx={{ fontSize: 80, mb: 2, opacity: 0.3 }}>🎬</Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}>Your wishlist is empty</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.4)", mb: 3, textAlign: "center", maxWidth: 300 }}>Start saving reels you love! Tap the heart icon on any product.</Typography>
            <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, borderRadius: "100px", px: 4, py: 1.2, "&:hover": { bgcolor: "#e6c512" } }}>Browse Reels</Button>
          </Box>
        ) : (
          <>
            {/* MOBILE: full-screen vertical swiper — subtract header height */}
            <Box sx={{ display: { xs: "block", sm: "none" }, height: "calc(100dvh - 112px)", width: "100%", overflow: "hidden" }}>
              <Swiper direction="vertical" modules={[Mousewheel, Keyboard]} mousewheel={{ forceToAxis: true }} keyboard={{ enabled: true }}
                onSlideChange={(swiper) => setActiveMobileIndex(swiper.activeIndex)}
                style={{ width: "100%", height: "100%", overflow: "hidden" }}>
                {wishlist.map((item, index) => (
                  <SwiperSlide key={item.id || item._id || item.productId} style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
                    <MobileReelCard item={item} isActive={index === activeMobileIndex} onNavigate={navigate} onAddToCart={handleAddToCart} onToggle={toggleWishlist} onShare={handleShare} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>

            {/* DESKTOP: grid layout, 6 cards per row */}
            <Box sx={{ display: { xs: "none", sm: "block" }, px: { xs: 2, sm: 4 }, maxWidth: 1600, mx: "auto" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 2 }}>
                {wishlist.map((item) => (
                  <DesktopCard key={item.id || item._id || item.productId} item={item} onNavigate={(path) => navigate(path)} onAddToCart={handleAddToCart} onToggle={toggleWishlist} onShare={handleShare} />
                ))}
              </Box>
            </Box>

            <Box sx={{ textAlign: "center", mt: 4, mb: 4, display: { xs: "none", sm: "block" } }}>
              <Button variant="text" onClick={() => navigate("/")} sx={{ color: "rgba(255,255,255,0.4)", textTransform: "none", fontSize: 13, "&:hover": { color: "#fff" } }}>← Browse more reels</Button>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default WishlistPage;